/**
 * DatePicker, pure half.
 *
 * Moved verbatim from components/FlowBuilder/flow-components/components/DatePickerComponent.js. Runs unchanged in the
 * browser and, via the flow-core bundle, inside GraalJS on the backend, so it
 * must never reach for React, the DOM, Node APIs, or the network.
 */

import { reservedFieldNameErrors } from '../fieldName';

export const DatePickerCore = {
  type: 'DatePicker',
  name: 'Date Picker',
  icon: '📅',
  description: 'Pick a single date from a date selector.',
  category: 'input',
  maxPerScreen: null,
  getDefaultProperties() {
    return {
      name: 'date_picker',
      label: 'Select date',
      required: false,
      'min-date': '',
      'max-date': '',
      'unavailable-dates': [],
      'helper-text': '',
      'init-value': '',
      'error-message': '',
      enabled: true,
      visible: true,
    };
  },
  getPropertySchema() {
    return {
      name: {
        type: 'string',
        label: 'Field Name',
        required: true,
      },
      label: {
        type: 'string',
        label: 'Label',
        required: true,
      },
      required: {
        type: 'boolean',
        label: 'Required',
        required: false,
      },
      'min-date': {
        type: 'string',
        label: 'Min Date (YYYY-MM-DD)',
        required: false,
      },
      'max-date': {
        type: 'string',
        label: 'Max Date (YYYY-MM-DD)',
        required: false,
      },
      'unavailable-dates': {
        type: 'array',
        label: 'Unavailable Dates',
        required: false,
        itemSchema: { type: 'string' },
      },
      'helper-text': {
        type: 'string',
        label: 'Helper Text',
        required: false,
      },
      'init-value': {
        type: 'string',
        label: 'Initial Value',
        required: false,
      },
      'error-message': {
        type: 'string',
        label: 'Error Message',
        required: false,
      },
      enabled: {
        type: 'boolean',
        label: 'Enabled',
        required: false,
      },
      visible: {
        type: 'boolean',
        label: 'Visible',
        required: false,
      },
    };
  },
  validateProperties(properties) {
    var errors = [];
    var warnings = [];
    var dateRegex = /^\d{4}-\d{2}-\d{2}$/;

    if (!properties.name || properties.name.trim().length === 0) {
      errors.push('Field name is required');
    }

    if (properties.name && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(properties.name)) {
      errors.push('Field name must start with letter or underscore');
    }

    errors.push(...reservedFieldNameErrors(properties.name));

    if (!properties.label || properties.label.trim().length === 0) {
      errors.push('Label is required');
    }

    if (properties.label && properties.label.length > 40) {
      errors.push('Label must be 40 characters or less');
    }

    if (properties['min-date'] && properties['max-date']) {
      var minDate = new Date(properties['min-date']);
      var maxDate = new Date(properties['max-date']);

      if (minDate > maxDate) {
        errors.push('Minimum date must be before maximum date');
      }
    }

    if (properties['min-date'] && !dateRegex.test(properties['min-date'])) {
      errors.push('Minimum date must be in YYYY-MM-DD format');
    }
    if (properties['max-date'] && !dateRegex.test(properties['max-date'])) {
      errors.push('Maximum date must be in YYYY-MM-DD format');
    }

    if (properties['init-value'] && !dateRegex.test(properties['init-value'])) {
      errors.push('Initial value must be in YYYY-MM-DD format');
    }

    if (properties['unavailable-dates'] && Array.isArray(properties['unavailable-dates'])) {
      properties['unavailable-dates'].forEach((date, index) => {
        if (!dateRegex.test(date)) {
          errors.push(`Unavailable date ${index + 1} must be in YYYY-MM-DD format`);
        }
      });
    }

    return { errors, warnings };
  },
  transformToWhatsApp(properties) {
    var result = {
      type: 'DatePicker',
      name: properties.name,
      label: properties.label,
      required: properties.required || false,
    };

    if (properties['min-date'] && properties['min-date'].trim()) {
      result['min-date'] = properties['min-date'];
    }

    if (properties['max-date'] && properties['max-date'].trim()) {
      result['max-date'] = properties['max-date'];
    }

    if (properties['unavailable-dates'] && properties['unavailable-dates'].length > 0) {
      // Filter out empty strings from the array
      var filteredDates = properties['unavailable-dates'].filter(d => d && d.trim());
      if (filteredDates.length > 0) {
        result['unavailable-dates'] = filteredDates;
      }
    }

    if (properties['helper-text'] && properties['helper-text'].trim()) {
      result['helper-text'] = properties['helper-text'];
    }

    if (properties['init-value'] && properties['init-value'].trim()) {
      result['init-value'] = properties['init-value'];
    }

    if (properties['error-message'] && properties['error-message'].trim()) {
      result['error-message'] = properties['error-message'];
    }

    if (properties.enabled === false) {
      result.enabled = false;
    }

    if (properties.visible === false) {
      result.visible = false;
    }

    return result;
  },
  transformFromWhatsApp(whatsappComponent) {
    var helperText = whatsappComponent['helper-text'];
    if (typeof helperText === 'object' && helperText !== null) {
      helperText = helperText.text || '';
    }

    var errorMessage = whatsappComponent['error-message'];
    if (typeof errorMessage === 'object' && errorMessage !== null) {
      errorMessage = errorMessage.text || '';
    }

    return {
      name: whatsappComponent.name,
      label: whatsappComponent.label,
      required: whatsappComponent.required || false,
      'min-date': whatsappComponent['min-date'] || '',
      'max-date': whatsappComponent['max-date'] || '',
      'unavailable-dates': whatsappComponent['unavailable-dates'] || [],
      'helper-text': helperText || '',
      'init-value': whatsappComponent['init-value'] || '',
      'error-message': errorMessage || '',
      enabled: whatsappComponent.enabled !== false,
      visible: whatsappComponent.visible !== false,
    };
  },
};
